/**
 * Which program BEST is showing, and swapping between them.
 *
 * The selection lives on the SERVER, in the registry, not in localStorage: it
 * follows a person between machines, and the same answer decides which
 * database every API request opens. Keeping a second copy in the browser would
 * mean two things that can disagree.
 *
 * Swapping is a hard context switch. Everything below the AppBar is about one
 * program, so a swap navigates home and remounts the screen rather than
 * trying to translate the current URL - /requirements/SUB_STR_2 is not
 * guaranteed to name anything in the program being switched to.
 */

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, setProjectSlug } from '../api'
import type { CurrentProject, ProjectRow } from '../schema'
import { ProjectContext } from './ProjectContext'
import type { ProjectState } from './ProjectContext'

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [current, setCurrent] = useState<ProjectRow | null>(null)
  const [branch, setBranch] = useState('')
  const [issue, setIssue] = useState('')
  const [singleDatabase, setSingleDatabase] = useState(false)
  const [loading, setLoading] = useState(true)
  const [unsaved, setUnsaved] = useState(false)

  const load = useCallback(async () => {
    const listing = await api.projects().catch(() => ({ items: [], current: null }))
    setProjects(listing.items)

    // 409 means nothing is registered, which is a state the picker handles
    // rather than an error: it is what a fresh install looks like.
    const resolved: CurrentProject | null = await api
      .currentProject()
      .catch(() => null)

    setSingleDatabase(Boolean(resolved?.single_database))
    setCurrent(resolved?.project ?? null)
    setBranch(resolved?.branch ?? '')
    setIssue(resolved?.issue ?? '')
    // Address subsequent requests explicitly. Until this point the server has
    // been choosing, which is right for the first load and wrong afterwards -
    // an explicit slug is what makes a swap take effect immediately.
    setProjectSlug(resolved?.project?.slug ?? '')
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const choose = useCallback(
    async (slug: string) => {
      const chosen = await api.chooseProject(slug)
      setProjectSlug(chosen.slug)
      setCurrent(chosen)
      setBranch(chosen.last_branch || chosen.default_branch)
      setIssue(chosen.last_issue)
      setUnsaved(false)
      // The listing carries per-person state, and choosing un-hides.
      const listing = await api.projects()
      setProjects(listing.items)
    },
    [],
  )

  const create = useCallback(
    async (body: { slug: string; name?: string; description?: string }) => {
      // The server switches the creator to it: the only reason to register a
      // program from here is to start working in it.
      const created = await api.createProject(body)
      setProjectSlug(created.slug)
      setCurrent(created)
      setBranch(created.default_branch)
      setIssue('')
      const listing = await api.projects()
      setProjects(listing.items)
      return created
    },
    [],
  )

  const value = useMemo<ProjectState>(
    () => ({
      projects,
      current,
      branch,
      issue,
      singleDatabase,
      loading,
      unsaved,
      setUnsaved,
      choose,
      create,
      reload: load,
    }),
    [projects, current, branch, issue, singleDatabase, loading, unsaved,
     choose, create, load],
  )

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
}

export function useProject(): ProjectState {
  const value = useContext(ProjectContext)
  if (!value) {
    throw new Error('useProject must be used inside a ProjectProvider')
  }
  return value
}
